import { apiRequest } from '../client'

export interface EdgeHub {
  id: number
  restaurantId: number
  restaurantName?: string
  fingerprint: string
  lastSeen: string | null
  online: boolean
  appliedBundleVersion: number | null
}

export interface EdgesListResponse {
  items: Array<EdgeHub>
}

export interface PairingCodeRequest {
  restaurantId: number
  ttlSeconds?: number
}

export interface PairingCodeResponse {
  code: string
  expiresAt: string
}

export interface RegisterEdgeRequest {
  pairingCode: string
  fingerprint?: string
  version?: string
}

export interface RegisterEdgeResponse {
  edgeId: number
  edgeKey: string
}

export interface ManualProvisionRequest {
  restaurantId: number
  name?: string
}

export const edgesApi = {
  /**
   * List all Edge hubs
   */
  list: async (): Promise<EdgesListResponse> => {
    const raw = await apiRequest<any>('/api/v0/admin/edges')
    const now = Date.now()
    const items: Array<EdgeHub> = (raw.items || []).map((r: any) => {
      const lastSeen: string | null = r.lastSeenAt ?? null
      const online = lastSeen ? now - Date.parse(lastSeen) <= 120_000 : false
      return {
        id: r.id,
        restaurantId: r.restaurantId,
        restaurantName: r.restaurantName,
        fingerprint: r.name, // backend uses 'name' for display/fingerprint
        lastSeen,
        online,
        appliedBundleVersion: r.currentBundleVersion ?? null,
      }
    })
    return { items }
  },

  /**
   * Create a pairing code for Edge registration
   */
  createPairingCode: (
    restaurantId: number,
    ttlSeconds: number = 600,
  ): Promise<PairingCodeResponse> =>
    apiRequest<PairingCodeResponse>('/api/v0/admin/pairing-codes', {
      method: 'POST',
      body: JSON.stringify({ restaurantId, ttlSeconds }),
    }),

  /**
   * Register an Edge using a pairing code.
   * Note: This endpoint is open (pairing code acts as bearer), so we skip auth headers.
   */
  register: (req: RegisterEdgeRequest): Promise<RegisterEdgeResponse> =>
    apiRequest<RegisterEdgeResponse>('/api/v0/edges/register', {
      method: 'POST',
      body: JSON.stringify(req),
      skipAuth: true,
    }),

  /**
   * Manually provision an Edge (no pairing).
   * Returns edgeId and edgeKey for manual YAML configuration on the device.
   */
  manualProvision: (
    req: ManualProvisionRequest,
  ): Promise<RegisterEdgeResponse> =>
    apiRequest<RegisterEdgeResponse>('/api/v0/admin/edges/manual-register', {
      method: 'POST',
      body: JSON.stringify(req),
    }),
}
